import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, type Project, type ProjectWeather } from '../lib/api';

/**
 * The weather panel — P4-8, §6.6.
 *
 * There is no geocoding provider in the vendor list (§6.2), so site
 * coordinates are a manual entry here — paste them from Google Maps or a
 * site survey — rather than a new paid API. `weather_group_key` is the
 * server's own derivation from whatever lat/lon land on `PATCH /site`; this
 * panel never computes or sends one itself.
 *
 * `GET /weather` is a pure cache read (§6.6: "none of them can trigger an
 * ad-hoc fetch"), so a freshly-saved coordinate pair reads `not_cached_yet`
 * until the scheduled refresh next runs — this panel says exactly that
 * rather than implying something is broken.
 *
 * §0.1: white/#F4F6FA surfaces only, no gold. Blocked/alert reads in danger
 * red, stale in warning amber, live conditions in success green — the same
 * tone system as HoldPanel.
 */

function formatCoord(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? String(n) : '';
}

export function WeatherPanel({ projectId }: { projectId: string }): JSX.Element {
  const [project, setProject] = useState<Project | null>(null);
  const [weather, setWeather] = useState<ProjectWeather | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');

  const load = useCallback(async () => {
    try {
      const { project: p } = await api.project(projectId);
      setProject(p);
      setLat(formatCoord(p.site_lat));
      setLon(formatCoord(p.site_lon));
      if (p.weather_group_key) {
        setWeather(await api.projectWeather(projectId));
      } else {
        setWeather({ state: 'no_site_coordinates' });
      }
      setError(null);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.body.message : 'The weather panel could not be read.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveCoordinates = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const latNum = Number(lat);
      const lonNum = Number(lon);
      await api.updateProjectSite(projectId, { siteLat: latNum, siteLon: lonNum });
      setNotice('Coordinates saved. The next scheduled refresh will populate current conditions.');
      setEditing(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.body.message : 'The coordinates were refused.');
    } finally {
      setBusy(false);
    }
  }, [projectId, lat, lon, load]);

  if (error && !project) {
    return (
      <section className="card mb-4 rounded-card border border-border bg-white p-4">
        <p className="text-body text-danger">{error}</p>
      </section>
    );
  }
  if (!project || !weather) {
    return (
      <section className="card mb-4 rounded-card border border-border bg-white p-4">
        <p className="text-body text-text-secondary">Reading site weather…</p>
      </section>
    );
  }

  const hasCoordinates = weather.state !== 'no_site_coordinates';
  const alerts = weather.state === 'ok' ? weather.alerts : [];
  const hasAlerts = Array.isArray(alerts) && alerts.length > 0;
  const tone =
    weather.state === 'ok'
      ? hasAlerts
        ? 'border-danger'
        : weather.stale
          ? 'border-warning'
          : 'border-success'
      : 'border-text-secondary';

  const showForm = editing || !hasCoordinates;

  return (
    <section
      aria-labelledby="weather-heading"
      className={`card mb-4 rounded-card border bg-white p-4 ${tone}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="weather-heading" className="text-h2 font-semibold text-text-primary">
          Site weather
        </h2>
        {hasCoordinates ? (
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="text-micro font-medium uppercase text-royal hover:text-navy"
          >
            {editing ? 'Cancel' : 'Edit coordinates'}
          </button>
        ) : null}
      </div>

      {notice ? (
        <p role="status" className="mt-2 text-body text-success">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-body text-danger">
          {error}
        </p>
      ) : null}

      {showForm ? (
        <div className="mt-3 grid gap-2">
          <p className="text-body text-text-secondary">
            {hasCoordinates
              ? 'Update the site coordinates.'
              : 'No site coordinates on file yet. Paste latitude/longitude from Google Maps or a site survey to enable NWS weather for this project.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <label className="flex flex-col gap-1">
              <span className="micro-label text-text-secondary">Latitude</span>
              <input
                type="text"
                inputMode="decimal"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                placeholder="30.4515"
                className="min-h-touch w-36 rounded-card border border-border bg-white px-3 py-2 text-body text-text-primary"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="micro-label text-text-secondary">Longitude</span>
              <input
                type="text"
                inputMode="decimal"
                value={lon}
                onChange={(e) => setLon(e.target.value)}
                placeholder="-91.1871"
                className="min-h-touch w-36 rounded-card border border-border bg-white px-3 py-2 text-body text-text-primary"
              />
            </label>
            <button
              type="button"
              disabled={busy || lat.trim() === '' || lon.trim() === ''}
              onClick={() => void saveCoordinates()}
              className="btn-primary min-h-touch self-end rounded-card px-4 text-body font-medium disabled:opacity-40"
            >
              Save coordinates
            </button>
          </div>
        </div>
      ) : null}

      {!showForm && weather.state === 'not_cached_yet' ? (
        <p className="mt-3 text-body text-text-secondary">
          Coordinates saved — awaiting the next scheduled refresh to populate current conditions.
        </p>
      ) : null}

      {!showForm && weather.state === 'ok' ? (
        <div className="mt-3 grid gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="numeric text-numeric text-text-primary">
              {Math.round(weather.current.tempF)}°F
            </span>
            <span className="text-body text-text-primary">{weather.current.summary}</span>
            {weather.stale ? (
              <span className="inline-flex items-center rounded-pill border border-warning px-2 py-0.5 text-micro uppercase text-warning">
                Stale
              </span>
            ) : null}
          </div>
          <p className="text-micro text-text-secondary">
            Last fetched {new Date(weather.fetchedAt).toLocaleString()}
          </p>
          {hasAlerts ? (
            <div className="mt-1 grid gap-2">
              {(alerts as { headline?: string; event?: string; severity?: string }[]).map((a, i) => (
                <div
                  key={i}
                  className="rounded-card border border-danger bg-white p-2 text-body text-danger"
                >
                  {a.headline ?? a.event ?? 'Active weather alert'}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
